// src/pages/photobook/Photobookproductpage.jsx
  import React, { useEffect, useState } from "react";
  import { Link, useNavigate, useParams } from "react-router-dom";
  import { Star, Check, ChevronDown } from "lucide-react";
  import { apiFetch } from "../../api/api";

  function FontImport() {
    return (
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap');
      .og-display{font-family:'Fraunces',serif} .og-body{font-family:'DM Sans',sans-serif}`}</style>
    );
  }

  function Stars({ value, size = 14 }) {
    const v = Math.round(parseFloat(value));
    return (
      <div className="flex gap-0.5">
        {[1,2,3,4,5].map(n => (
          <Star key={n} size={size} className={n <= v ? "fill-[#1A1A1A] text-[#1A1A1A]" : "fill-none text-[#1A1A1A]/30"} />
        ))}
      </div>
    );
  }

  function addBusinessDays(start, days) {
    const d = new Date(start);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d;
  }
  function fmt(d) {
    return d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  }

  // ── Gallery ───────────────────────────────────────────────────────────────────
  function Gallery({ images, name, activeIndex, onSelect }) {
    return (
      <div className="flex gap-2.5 sm:gap-3">
        <div className="flex flex-col gap-1.5 sm:gap-2 overflow-y-auto max-h-[420px] sm:max-h-[520px] pr-1">
          {images.map((img, i) => (
            <button key={i} onClick={() => onSelect(i)}
              className={`flex-shrink-0 h-14 w-12 sm:h-16 sm:w-14 rounded-md overflow-hidden border-2 transition-all ${
                activeIndex===i ? "border-[#1A1A1A]" : "border-gray-200 opacity-60 hover:opacity-100"}`}>
              <img src={img} alt={`view ${i+1}`} className="h-full w-full object-cover"/>
            </button>
          ))}
        </div>
        <div className="flex-1 aspect-square overflow-hidden rounded-xl bg-white relative">
          <img src={images[activeIndex]} alt={name} className="h-full w-full object-contain p-3 sm:p-4"/>
        </div>
      </div>
    );
  }

  const FORMAT_OPTIONS = ["Square"];
  const SIZE_OPTIONS = {
    Square: ['Mini (7" x 7")', 'Medium (10" x 8")'],
  };
  const SIZE_PRICE = {
    'Mini (7" x 7")': 0,
    'Medium (10" x 8")': 0,
  };

  function SelectDropdown({ label, value, options, onChange }) {
    const [open, setOpen] = useState(false);
    return (
      <div className="relative">
        <p className="mb-2 text-sm font-semibold">{label}</p>
        <button type="button" onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none">
          <span>{value}</span>
          <ChevronDown size={16} className={`text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}/>
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)}/>
            <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
              {options.map(o => (
                <button key={o} type="button"
                  onClick={() => { onChange(o); setOpen(false); }}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors hover:bg-gray-50 ${value===o ? "font-semibold bg-gray-50" : ""}`}>
                  {o}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Section 1: Flick Through ──────────────────────────────────────────────────
  const FLICK_TABS = [
    { label:"Family & friends", img:"https://oddgiraffe.com/cdn/shop/files/DLY-1_Daily_Planning.jpg?v=1695223660" },
    { label:"Special ones",     img:"https://oddgiraffe.com/cdn/shop/files/DLY-2_Weekly_Overview.jpg?v=1695223660" },
    { label:"Wedding day",      img:"https://oddgiraffe.com/cdn/shop/files/DLY-3_Benefits_of_Daily_Planning.jpg?v=1695223661" },
    { label:"Travel & adventure", img:"https://oddgiraffe.com/cdn/shop/files/DLY-4_Quick_Guide.jpg?v=1695223661" },
    { label:"Memories",         img:"https://oddgiraffe.com/cdn/shop/files/DLY-5_Plan_Your_Week.jpg?v=1695223661" },
  ];

  function FlickThroughSection({ productName }) {
    const [active, setActive] = useState(0);
    // (kept as-is, still commented out in the original file)
  }

  // ── Section 2: Customer Review Quote ─────────────────────────────────────────
  const REVIEW_QUOTES = [
    { quote:"Perfect gift idea ... great quality, easy to make [and] speedy delivery. Would definitely recommend.", name:"RHEA SHETH" },
    { quote:"I am so happy about the product. My wife is going to present his sister the first album of his son.", name:"JEEVAN KUMAR GUDLA" },
    { quote:"The print is beautiful. I loved it. Best way to preserve the memories.", name:"SHRUTI" },
  ];

  function ReviewQuoteSection() {
    const [idx, setIdx] = useState(0);
    const q = REVIEW_QUOTES[idx];
    return (
      <section className="bg-[#E8E0F0] py-12 sm:py-20 px-4">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="og-display text-2xl sm:text-3xl md:text-4xl text-[#1A1A1A] mb-3 sm:mb-4 leading-snug px-1">
            Here's what our other customers have to say
          </h2>
          <Link to="#" className="text-sm font-semibold underline underline-offset-4 text-[#1A1A1A] mb-8 sm:mb-12 inline-block">
            Read all of our reviews
          </Link>

          <blockquote className="og-display text-xl sm:text-2xl md:text-3xl italic text-[#1A1A1A] leading-relaxed mt-6 sm:mt-8 mb-5 sm:mb-6 px-1">
            "{q.quote}"
          </blockquote>
          <p className="text-sm font-bold tracking-widest text-gray-500">– {q.name}</p>

          <div className="flex items-center justify-center gap-2 mt-7 sm:mt-8">
            {REVIEW_QUOTES.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-all ${i===idx ? "bg-[#1A1A1A] scale-125" : "bg-gray-400"}`}/>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Section 3: Feature Cards ──────────────────────────────────────────────────
  const FEATURES = [
    {
      img:"https://oddgiraffe.com/cdn/shop/files/1760_1363247459750104181_n_1_1.jpg?v=1654772067",
      title:"Thoughtful, personal, lasting",
      desc:"Each journal is made with premium 100gsm paper with highest quality 3mm thick hardback cover along with 170gsm silk-paper finish",
    },
    {
      img:"https://oddgiraffe.com/cdn/shop/files/224240788_943586059552275_596654800752328921_n_1.jpg?v=1654772077",
      title:"Sustainable products & delivery",
      desc:"All our products & packaging materials are made specially for you upon order to avoid wastage with wood-free or FSC Certified paper",
    },
    {
      img:"https://oddgiraffe.com/cdn/shop/files/72153039_159773952004760_1363247459750104181_n_1.jpg?v=1654772053",
      title:"Perfect size",
      desc:"Our planners are sized perfectly to be comfortable for carrying around while having enough space for all your plans and thoughts",
    },
  ];

  function FeatureCardsSection() {
    return (
      <section className="bg-white py-10 sm:py-16 px-4">
        <div className="mx-auto max-w-5xl grid grid-cols-1 sm:grid-cols-3 gap-7 sm:gap-8">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex flex-col items-center text-center">
              <div className="w-full aspect-[4/3] overflow-hidden rounded-xl mb-4 sm:mb-5">
                <img src={f.img} alt={f.title} className="w-full h-full object-cover"/>
              </div>
              <h3 className="og-display text-base sm:text-lg font-semibold text-[#1A1A1A] mb-2">{f.title}</h3>
              <p className="text-[13.5px] sm:text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // ── Section 4: Reviews Grid ───────────────────────────────────────────────────
  const SEED_REVIEWS = [
    { name:"maithili",            verified:true, rating:5, title:"Excellent!!",                          body:"Great Quality. Can't wait for the new year to buy planners for me and my friends.",  date:"23/06/2026" },
    { name:"Shruti",              verified:true, rating:5, title:"Worthy Buy!",                           body:"The print is beautiful. I loved it. Best way to preserve the memories.",             date:"23/06/2026" },
    { name:"Jeevan Kumar Gudla",  verified:true, rating:5, title:"I am so happy about the product",       body:"I am so happy about the product. My wife is happy that she is going to present his sister the first album of his son, as they don't have anything previously.", date:"23/06/2026" },
    { name:"Sandeep Yellinedi",   verified:true, rating:5, title:"A perfect way to remember your travels",body:"A perfect way to remember your travels or special occasions",                       date:"23/06/2026" },
    { name:"Ranjith",             verified:true, rating:5, title:"Good",                                  body:"Good",                                                                             date:"22/06/2026" },
    { name:"Priya Sharma",        verified:true, rating:5, title:"Amazing gift!",                         body:"Gifted this to my best friend and she absolutely loved it. The quality is superb!", date:"20/06/2026" },
  ];

  function ReviewCard({ review }) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 flex flex-col gap-2.5 sm:gap-3">
        <div className="flex items-start justify-between gap-2">
          <Stars value={review.rating} size={13}/>
          <span className="text-xs text-gray-400 flex-shrink-0">{review.date}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-4 h-4 text-gray-500">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold">{review.name}</span>
              {review.verified && (
                <span className="bg-black text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm">Verified</span>
              )}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <svg viewBox="0 0 24 24" className="w-3 h-3 text-gray-400" fill="currentColor">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span className="text-[11px] text-gray-400">India</span>
            </div>
          </div>
        </div>
        {review.title && <p className="text-sm font-bold">{review.title}</p>}
        <p className="text-sm text-gray-600 leading-relaxed">{review.body}</p>
      </div>
    );
  }

  function ReviewsGridSection() {
    const avg = (SEED_REVIEWS.reduce((s,r)=>s+r.rating,0)/SEED_REVIEWS.length).toFixed(1);
    const [writeOpen, setWriteOpen] = useState(false);
    const [form, setForm] = useState({ name:"", rating:5, title:"", body:"" });
    const [submitted, setSubmitted] = useState(false);
    const set = (k,v) => setForm(f=>({...f,[k]:v}));

    const handleSubmit = (e) => {
      e.preventDefault();
      if (!form.name || !form.body) return;
      setSubmitted(true);
    };

    return (
      <section className="bg-[#FAFAFA] py-10 sm:py-16 px-4">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between mb-4 sm:mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
              <Stars value={avg} size={16}/>
              <span className="text-[13px] sm:text-sm text-gray-500">{SEED_REVIEWS.length} reviews</span>
              <span className="text-[13px] sm:text-sm font-bold">{avg} / 5</span>
            </div>
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-2">
                Most Recent
                <ChevronDown size={13}/>
              </div>
              <button onClick={() => setWriteOpen(o=>!o)}
                className="px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full border-2 border-[#1A1A1A] text-[11px] sm:text-xs font-bold hover:bg-[#1A1A1A] hover:text-white transition-colors whitespace-nowrap">
                Write a Review
              </button>
            </div>
          </div>

          {writeOpen && !submitted && (
            <form onSubmit={handleSubmit} className="mb-6 border border-gray-200 rounded-xl p-4 sm:p-5 bg-white flex flex-col gap-3.5 sm:gap-4">
              <h3 className="font-bold text-sm">Write a Review</h3>
              <input placeholder="Your name *" value={form.name} onChange={e=>set("name",e.target.value)}
                className="border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:border-[#1A1A1A] focus:outline-none"/>
              <input placeholder="Review title" value={form.title} onChange={e=>set("title",e.target.value)}
                className="border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:border-[#1A1A1A] focus:outline-none"/>
              <textarea placeholder="Your review *" value={form.body} onChange={e=>set("body",e.target.value)} rows={3}
                className="border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:border-[#1A1A1A] focus:outline-none resize-none"/>
              <p className="text-[11px] text-gray-400">Your review will appear after admin approval.</p>
              <button type="submit" className="bg-[#1A1A1A] text-white rounded-md py-2.5 text-sm font-bold hover:opacity-90">Submit Review</button>
            </form>
          )}
          {submitted && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-center text-sm text-green-700 font-semibold">
              ✓ Thank you! Your review has been submitted for approval.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-4">
            {SEED_REVIEWS.map((r,i) => <ReviewCard key={i} review={r}/>)}
          </div>
        </div>
      </section>
    );
  }

  // ── Main ──────────────────────────────────────────────────────────────────────
  export default function PhotobookProductPage() {
    const { slug }   = useParams();
    const navigate   = useNavigate();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const [format, setFormat]           = useState("Square");
  const [size, setSize] = useState('Mini (7" x 7")');
    const [colorIdx, setColorIdx]       = useState(0);
    const [openSection, setOpenSection] = useState("");
    const [activeImage, setActiveImage] = useState(0);


  useEffect(() => {
    const idx = SIZE_OPTIONS[format]?.indexOf(size);
    console.log("size:", size, "idx:", idx, "images:", product?.images);
    if (idx !== -1 && product?.images?.[idx]) {
      setActiveImage(idx);
    } else {
      setActiveImage(0);
    }
  }, [size, format, product]);

  useEffect(() => { setSize(SIZE_OPTIONS[format][0]); }, [format]);

    // Slug badalte hi backend se product fetch karo (hardcoded Photobooks.js hata diya)
    useEffect(() => {
      let cancelled = false;
      window.scrollTo({ top:0, behavior:"smooth" });
    setFormat("Square"); setSize('Mini (7" x 7")'); setColorIdx(0);

      async function load() {
        setLoading(true);
        setNotFound(false);
        try {
          const res = await apiFetch(`/public/product.php?slug=${encodeURIComponent(slug)}`);
          if (cancelled) return;
          const r = res.data;
          setProduct({
            ...r,
            colors: r.swatches || [],       // public API field is "swatches"
            highlights: r.bullets || [],    // public API field is "bullets"
            specs: r.bullets || [],         // no separate specs table yet — reusing highlights for now
          });
        } catch (err) {
          if (!cancelled) setNotFound(true);
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
      load();
      return () => { cancelled = true; };
    }, [slug]);

    if (loading) {
      return (
        <div className="og-body grid min-h-screen place-items-center bg-white">
          <FontImport/>
          <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-[#1A1A1A] animate-spin"/>
        </div>
      );
    }

    if (notFound || !product) {
      return (
        <div className="og-body grid min-h-screen place-items-center bg-white px-6 text-center">
          <FontImport/>
          <div>
            <h1 className="og-display text-3xl text-[#1A1A1A]">We couldn't find that photobook</h1>
            <p className="mt-2 text-[#6B655C]">It may have sold out or the link is off.</p>
            <button onClick={() => navigate("/photobook")}
              className="mt-6 rounded-full bg-[#1A1A1A] px-6 py-2.5 text-sm font-semibold text-white">
              Back to Photobooks
            </button>
          </div>
        </div>
      );
    }

    const extraPrice = SIZE_PRICE[size] || 0;
  const finalPrice = product.price + extraPrice;
    const finalCompare = (product.comparePrice || product.price) + extraPrice;
    const shipStart = addBusinessDays(new Date(), 6);
    const shipEnd   = addBusinessDays(new Date(), 8);

    return (
      <div className="og-body min-h-screen bg-white text-[#1A1A1A]">
        <FontImport/>

        {/* Breadcrumb */}
        <div className="mx-auto max-w-7xl px-4 pt-4 sm:pt-6 sm:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-[#6B655C]">
            <Link to="/" className="hover:text-[#1A1A1A]">Home</Link>
            <span>/</span>
            <Link to="/photobook" className="hover:text-[#1A1A1A]">Photobooks</Link>
            <span>/</span>
            <span className="text-[#1A1A1A]">{product.name}</span>
          </nav>
        </div>

        <main className="mx-auto grid max-w-7xl gap-7 sm:gap-10 px-4 py-6 sm:py-8 sm:px-6 lg:grid-cols-2 lg:gap-16">
          <div className="lg:sticky lg:top-4 lg:self-start">
          <Gallery
    images={product.images}
    name={product.name}
    activeIndex={activeImage}
    onSelect={setActiveImage}
  />
          </div>

          <div className="og-body">
            <h1 className="og-display text-[26px] sm:text-4xl leading-tight">{product.name}</h1>
            <p className="text-sm text-[#6B655C] mt-1">{product.subtitle}</p>

            <div className="mt-4 flex items-baseline gap-2.5 sm:gap-3">
              <span className="text-xl sm:text-2xl font-bold">Rs. {finalPrice.toLocaleString("en-IN")}</span>
              {product.comparePrice > product.price && (
                <span className="rounded bg-[#fce954] px-2 py-0.5 text-[13px] sm:text-sm font-semibold text-[#1A1A1A] line-through">
                  Rs. {finalCompare.toLocaleString("en-IN")}
                </span>
              )}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <Stars value={product.rating}/>
              <span className="text-sm text-[#6B655C]">{product.reviews} reviews</span>
            </div>
            <p className="text-xs text-[#6B655C] mt-0.5">
              We're rated {product.rating} out of 5 from over 1000+ reviews
            </p>
            <Link to="#" className="text-xs font-semibold underline underline-offset-2 text-[#1A1A1A]">
              See customer reviews
            </Link>

            {/* NEW — bold WhatsApp fallback line, requested by client */}
            <p className="mt-4 text-sm sm:text-[15px] font-bold text-[#1A1A1A]">
              Unable to create it yourself? Send your photos on our WhatsApp number and we'll create and send it to you.
            </p>

            <div className="mt-6 flex flex-col gap-4">
              <SelectDropdown label="Format" value={format} options={FORMAT_OPTIONS} onChange={setFormat}/>
              <SelectDropdown label="Size" value={size} options={SIZE_OPTIONS[format]} onChange={setSize}/>
              {product.colors?.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-semibold">Color</p>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((c, i) => (
                      <button key={i} onClick={() => setColorIdx(i)} aria-label={`Color ${i+1}`}
                        className={`h-9 w-9 sm:h-10 sm:w-10 rounded-full border-2 transition-all ${
                          colorIdx===i ? "border-[#1A1A1A] scale-110 shadow-md" : "border-gray-200 hover:border-gray-400"}`}
                        style={{ backgroundColor: c }}/>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* CTA — "Add to Bag" ki jagah editor pe le jaata hai, jaisa pehle se tha */}
    <button onClick={() => navigate(`/photobook/${slug}/editor`, {
    state:{
      format, size, colorIdx, productName: product.name,
      productSlug: slug,
      starterMode: product.photobookStarterMode,
      starterTemplateId: product.photobookTemplateId,
    }
  })}
    className="mt-7 w-full rounded-md bg-[#1A1A1A] py-3.5 sm:py-4 text-sm font-bold text-white transition-opacity hover:opacity-90">
    Start Editing
  </button>

            <div className="mt-5 rounded-md border border-gray-200 p-3.5 sm:p-4">
              <p className="text-sm font-semibold">Delivery</p>
              <p className="mt-1 text-sm text-[#C1432B]">
                Ships between: <strong>{fmt(shipStart)}</strong> and <strong>{fmt(shipEnd)}</strong>
              </p>
              <p className="mt-2 text-xs leading-relaxed text-[#6B655C]">
                Every product is made to order with care. Orders are typically shipped within 6 working days,
                excluding weekends. Once shipped, delivery usually takes 2–3 days.
              </p>
            </div>

            {/* Product Highlights Box */}
            {product.highlights?.length > 0 && (
              <div className="mt-5 rounded-md border border-[#1A1A1A]/10 p-4 sm:p-5">
                <p className="text-sm font-bold mb-3">Product Highlights</p>
                <ul className="flex flex-col gap-2">
                  {product.highlights.map(h => (
                    <li key={h} className="flex items-start gap-2 text-sm text-[#3F3A33]">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1A1A1A] flex-shrink-0"/>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-5 divide-y divide-gray-200 border-y border-gray-200">
              {[
                { id:"highlights", label:"Product Highlights" },
                { id:"specs",      label:"Dimensions & Paper Quality" },
                { id:"shipping",   label:"Shipping & Returns" },
              ].map((s) => (
                <div key={s.id}>
                  <button onClick={() => setOpenSection(openSection===s.id ? "" : s.id)}
                    className="flex w-full items-center justify-between py-4 text-left text-sm font-semibold">
                    {s.label}
                    <span className="og-display text-lg">{openSection===s.id ? "–" : "+"}</span>
                  </button>
                  {openSection===s.id && (
                    <div className="pb-4 text-sm leading-relaxed text-[#3F3A33]">
                      {s.id==="highlights" && (
                        <ul className="space-y-2">
                          {(product.highlights || []).map((h) => (
                            <li key={h} className="flex items-start gap-2">
                              <Check size={14} className="mt-0.5 flex-shrink-0"/>{h}
                            </li>
                          ))}
                        </ul>
                      )}
                      {s.id==="specs" && (
                        <ul className="space-y-1.5 list-disc pl-5">
                          {(product.specs || []).map(sp => <li key={sp}>{sp}</li>)}
                        </ul>
                      )}
                      {s.id==="shipping" && (
                        <p>Made to order. Due to personalised nature, we don't accept returns. Unhappy? Email support@aishcraft.com within 3 days of receipt.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </main>

        {/* ── New Sections ── */}
        <FlickThroughSection productName={product.name}/>
        <ReviewQuoteSection/>
        <FeatureCardsSection/>
        <ReviewsGridSection/>
      </div>
    );
  }